import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AyurvedaCard, AyurvedaCardHeader, AyurvedaCardTitle, AyurvedaCardContent } from '@/components/ui/ayurveda-card';
import { AyurvedaButton } from '@/components/ui/ayurveda-button';
import { PageTransition, StaggerContainer, StaggerItem, FadeInUp } from '@/components/ui/page-transition';
import { LotusIcon, LeafIcon, PulseIcon } from '@/components/ui/ayurveda-icons';
import { Button } from '@/components/ui/button';
import { Users, FileText, Calendar, Package, TrendingUp, Clock, Plus, RefreshCw } from 'lucide-react';
import API_BASE_URL from '@/config';

// Icon map for rendering icons by name
const iconMap = {
  Users,
  FileText,
  Calendar,
  Package,
  TrendingUp,
  Clock,
  Plus
};

// Fallback resolver so incoming API data without iconName still renders safely
const resolveIconName = (stat) => {
  if (!stat) return 'Users';
  if (stat.iconName && iconMap[stat.iconName]) return stat.iconName;
  if (stat.icon && iconMap[stat.icon]) return stat.icon;

  // Derive from title as a final fallback
  const title = (stat.title || '').toLowerCase();
  if (title.includes('patient')) return 'Users';
  if (title.includes('follow')) return 'Calendar';
  if (title.includes('prescription')) return 'FileText';
  if (title.includes('inventory') || title.includes('stock')) return 'Package';
  return 'Users';
};

// Dashboard component
export const Dashboard = () => {
  const [stats, setStats] = useState([
    { title: 'Total Patients', value: '0', trend: '—', iconName: 'Users' },
    { title: 'Upcoming Follow-ups', value: '0', trend: '—', iconName: 'Calendar' },
    { title: 'Prescriptions', value: '0', trend: '—', iconName: 'FileText' },
    { title: 'Medicine Stock', value: '0', trend: '—', iconName: 'Package' }
  ]);
  const [upcomingFollowups, setUpcomingFollowups] = useState([]);
  const [recentPatients, setRecentPatients] = useState([]);
  const [previousStats, setPreviousStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const statsResponse = await axios.get(`${API_BASE_URL}/api/dashboard/stats`);
      console.log('Dashboard stats:', statsResponse.data);

      // Calculate trend based on previous stats
      const newStats = statsResponse.data.map((stat) => {
        let trend = '—';
        if (previousStats) {
          const prevStat = previousStats.find(s => s.title === stat.title);
          if (prevStat) {
            const prevValue = parseInt(prevStat.value) || 0;
            const newValue = parseInt(stat.value) || 0;
            const change = newValue - prevValue;
            if (change > 0) {
              trend = `+${change}`;
            } else if (change < 0) {
              trend = `${change}`;
            } else {
              trend = 'No change';
            }
          }
        }
        const iconName = resolveIconName(stat);
        return { ...stat, iconName, trend };
      });

      setPreviousStats(statsResponse.data);
      setStats(newStats);

      const followupsResponse = await axios.get(`${API_BASE_URL}/api/followups/upcoming`);
      const patientsResponse = await axios.get(`${API_BASE_URL}/api/patients/recent`);

      setUpcomingFollowups(followupsResponse.data || []);
      setRecentPatients(patientsResponse.data || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();

    // Refresh every 10 seconds
    const interval = setInterval(fetchDashboardData, 10000);
    return () => clearInterval(interval);
  }, []);

  const quickActions = [
    {
      title: 'Add New Patient',
      iconName: 'Users',
      link: '/admin/patients/new',
      variant: 'primary',
      description: 'Register a new patient'
    },
    {
      title: 'Create Prescription',
      iconName: 'FileText',
      link: '/admin/prescriptions/new',
      variant: 'secondary',
      description: 'Write new prescription'
    },
    {
      title: 'Upload Prescription',
      iconName: 'FileText',
      link: '/admin/prescriptions/upload',
      variant: 'wellness',
      description: 'Upload existing prescription'
    },
    {
      title: 'Manage Inventory',
      iconName: 'Package',
      link: '/admin/inventory',
      variant: 'outline',
      description: 'Update medicine stock'
    }
  ];

  return (
    <PageTransition className="space-y-8">
      {/* Header */}
      <FadeInUp>
        <div className="glass-subtle p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-ayurveda-sage-200">
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex-shrink-0"
              >
                <img src="/logo.png" alt="Sanjivani Ayurvedic Clinic" className="h-12 sm:h-20 w-auto object-contain" />
              </motion.div>
              <div>
                <h1 className="ayurveda-heading text-2xl sm:text-3xl md:text-4xl font-bold text-ayurveda-sage-700">
                  Wellness Dashboard
                </h1>
                <p className="text-xs sm:text-base text-ayurveda-sage-600">Welcome back, Dr. Singh. Here's your healing sanctuary overview.</p>
              </div>
            </div>
            {/* Mobile Icon-only Refresh Button */}
            <Button
              variant="outline"
              size="icon"
              onClick={fetchDashboardData}
              disabled={loading}
              className="sm:hidden h-9 w-9 flex-shrink-0"
              aria-label="Refresh dashboard"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            {/* Desktop Refresh Button */}
            <Button
              variant="outline"
              onClick={fetchDashboardData}
              disabled={loading}
              className="hidden sm:inline-flex h-10 gap-1.5 self-center"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </FadeInUp>

      {/* Stats Grid (2 columns on mobile) */}
      <StaggerContainer className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-6">
        {stats.map((stat, index) => {
          const Icon = iconMap[stat.iconName] || iconMap[resolveIconName(stat)];
          const trendValue = stat.trend || '—';
          const isNegativeTrend = trendValue.startsWith('-');
          const trendTone = trendValue === 'No change'
            ? 'text-ayurveda-sage-500'
            : isNegativeTrend
              ? 'text-ayurveda-rose-500'
              : 'text-ayurveda-herbal-500';
          return (
            <StaggerItem key={index} className="h-full min-w-0">
              <AyurvedaCard className="transition-all duration-500 h-full">
                <AyurvedaCardContent className="p-3 sm:p-6 h-full flex flex-col justify-between min-w-0">
                  <div className="flex items-start justify-between gap-1.5">
                    <div className="space-y-1 sm:space-y-2 flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-ayurveda-sage-600 truncate">{stat.title}</p>
                      <p className="ayurveda-heading text-xl sm:text-3xl font-bold text-ayurveda-sage-700">{stat.value}</p>
                    </div>
                    <motion.div
                      className="bg-gradient-to-br from-ayurveda-sage-100 to-ayurveda-herbal-100 p-2 sm:p-3 rounded-lg sm:rounded-xl flex-shrink-0"
                      whileHover={{ scale: 1.1, rotate: 10 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Icon className="h-4 w-4 sm:h-6 sm:w-6 text-ayurveda-sage-600" />
                    </motion.div>
                  </div>
                  <div className="mt-2 sm:mt-4 flex items-center justify-between text-[10px] sm:text-xs">
                    <motion.span
                      whileHover={{ scale: 1.05 }}
                      className={`${trendTone} inline-flex items-center gap-1 font-semibold truncate`}
                    >
                      <TrendingUp className="h-3 w-3 flex-shrink-0" />
                      {trendValue === 'No change' ? 'Flat' : trendValue}
                    </motion.span>
                    <span className="text-ayurveda-sage-400 hidden xs:inline text-[10px] sm:text-xs">vs last load</span>
                  </div>
                </AyurvedaCardContent>
              </AyurvedaCard>
            </StaggerItem>
          );
        })}
      </StaggerContainer>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Upcoming Follow-ups */}
        <FadeInUp delay={0.2}>
          <AyurvedaCard className="h-full">
            <AyurvedaCardHeader className="border-b border-ayurveda-sage-200 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-br from-ayurveda-herbal-500 to-ayurveda-sage-500 p-2 rounded-lg">
                    <Calendar className="h-5 w-5 text-white" />
                  </div>
                  <AyurvedaCardTitle className="text-xl">Upcoming Follow-ups</AyurvedaCardTitle>
                </div>
                <AyurvedaButton variant="ghost" size="sm" asChild>
                  <Link to="/admin/followups">View All</Link>
                </AyurvedaButton>
              </div>
            </AyurvedaCardHeader>
            <AyurvedaCardContent className="p-0">
              <div className="divide-y divide-ayurveda-sage-100">
                {upcomingFollowups.length > 0 ? upcomingFollowups.map((followup) => (
                  <motion.div
                    key={followup.id}
                    className="p-4 hover:bg-ayurveda-sage-50 transition-colors cursor-pointer"
                    whileHover={{ x: 5 }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-1 flex-1">
                        <p className="font-medium text-ayurveda-sage-700">{followup.patient_name}</p>
                        <p className="text-sm text-ayurveda-sage-500">{followup.phone || 'No phone'}</p>
                      </div>
                      <div className="text-right text-sm">
                        <p className="font-medium text-ayurveda-herbal-600">{new Date(followup.date).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </motion.div>
                )) : (
                  <div className="p-8 text-center text-ayurveda-sage-400">
                    <PulseIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No upcoming follow-ups</p>
                  </div>
                )}
              </div>
            </AyurvedaCardContent>
          </AyurvedaCard>
        </FadeInUp>

        {/* Recent Patients */}
        <FadeInUp delay={0.4}>
          <AyurvedaCard className="h-full">
            <AyurvedaCardHeader className="border-b border-ayurveda-sage-200 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-br from-ayurveda-gold-500 to-ayurveda-sandalwood-500 p-2 rounded-lg">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <AyurvedaCardTitle className="text-xl">Recent Patients</AyurvedaCardTitle>
                </div>
                <AyurvedaButton variant="ghost" size="sm" asChild>
                  <Link to="/admin/patients">View All</Link>
                </AyurvedaButton>
              </div>
            </AyurvedaCardHeader>
            <AyurvedaCardContent className="p-0">
              <div className="divide-y divide-ayurveda-sage-100">
                {recentPatients.length > 0 ? recentPatients.map((patient) => (
                  <Link
                    key={patient.id}
                    to={`/admin/patients/${patient.id}`}
                    className="block"
                  >
                    <motion.div
                      className="p-4 hover:bg-ayurveda-sage-50 transition-colors"
                      whileHover={{ x: 5 }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <p className="font-medium text-ayurveda-sage-700">{patient.name}</p>
                          <p className="text-sm text-ayurveda-sage-500">
                            {patient.age} years • {patient.gender}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-ayurveda-sage-400">
                          <Clock className="h-4 w-4" />
                          <span>{(patient.lastVisit || patient.last_visit) ? new Date(patient.lastVisit || patient.last_visit).toLocaleDateString() : '—'}</span>
                        </div>
                      </div>
                    </motion.div>
                  </Link>
                )) : (
                  <div className="p-8 text-center text-ayurveda-sage-400">
                    <LeafIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No recent patients</p>
                  </div>
                )}
              </div>
            </AyurvedaCardContent>
          </AyurvedaCard>
        </FadeInUp>
      </div>

      {/* Quick Actions */}
      <FadeInUp delay={0.6}>
        <AyurvedaCard>
          <AyurvedaCardHeader>
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-ayurveda-sage-500 to-ayurveda-herbal-500 p-2 rounded-lg">
                <Plus className="h-5 w-5 text-white" />
              </div>
              <AyurvedaCardTitle className="text-xl">Quick Actions</AyurvedaCardTitle>
            </div>
          </AyurvedaCardHeader>
          <AyurvedaCardContent>
            <StaggerContainer className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              {quickActions.map((action, index) => {
                const Icon = iconMap[action.iconName] || Users;
                return (
                  <StaggerItem key={index}>
                    <AyurvedaButton
                      asChild
                      variant={action.variant}
                      className="h-auto py-4 sm:py-6 flex-col gap-2 sm:gap-3 w-full group"
                    >
                      <Link to={action.link}>
                        <motion.div
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Icon className="h-6 w-6" />
                        </motion.div>
                        <div className="text-center">
                          <div className="font-medium">{action.title}</div>
                          <div className="text-xs opacity-80 mt-1">{action.description}</div>
                        </div>
                      </Link>
                    </AyurvedaButton>
                  </StaggerItem>
                );
              })}
            </StaggerContainer>
          </AyurvedaCardContent>
        </AyurvedaCard>
      </FadeInUp>
    </PageTransition>
  );
};

export default Dashboard;
